import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Box, Checkbox, FormControlLabel, TextField, Tooltip } from "@mui/material";
import * as React from "react";
import { ToolButton } from "ui/Scripts/source/components/framework/Button/ToolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as riskService from "../../../../models/services/riskService";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { ListHeaderLine } from "source/components/framework/List/ListLine";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { AddToolButton } from "source/components/framework/Button/AddToolButton";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { IRiskClassificationNode, RiskClassificationNode } from "ui/Scripts/source/models/risk/riskClassificationNode";
import { IRiskClassificationNodes } from "ui/Scripts/source/models/risk/riskClassificationNodes";
import { useCallback, useEffect, useState } from "react";
import { RiskClassificationTreeNode } from "./RiskClassificationTreeNode";
import { RiskCategoryDialog } from "../RiskCategoryDialog/RiskCategoryDialog";
import { SaveDataObj } from "source/framework/saveData";
import { RiskProcessDialog } from "../RiskProcessDialog";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { AppSaveIcon } from "source/components/framework/Icon/AppSaveIcon";
import { AppCancelIcon } from "source/components/framework/Icon/AppCancelIcon";
import { EditToolButton } from "source/components/framework/Button/EditToolButton";
import { RiskStandardDialog } from "../RiskStandardDialog/RiskStandardDialog";
import { RiskStakeholderDialog } from "../RiskStakeholderDialog";

/* eslint-disable no-unused-vars */
export enum RiskClassificationType {
    RiskCategory = "0",
    RiskProcess = "1",
    RiskStakeholder = "2",
    RiskStandard = "3"
}
/* eslint-enable no-unused-vars */

interface IRiskClassificationNodesTreeProp {
    riskClassificationType: RiskClassificationType;
    companyId: string;
    infoText: string;
    titleTitle?: string;
    title?: string;
    inUseTitle?: string;
    inUse?: boolean;
    onSaveInUseAndTitle?: (inUse: boolean, title: string) => void;
}

interface IRiskClassificationNodesTreeState {
    title: string;
    titleEditMode: boolean;

    // Tree
    items: IRiskClassificationNode[];
    selectedId: string;
    selectedCanBeRemoved: boolean;

    // Dialog
    showEditDialog: boolean;
    editDialogId: string;
    editDialogParentId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
}

export const RiskClassificationNodesTree = (props: IRiskClassificationNodesTreeProp) => {
    const getStateFromRiskClassificationNodes = (aState: IRiskClassificationNodesTreeState,
        nodes: IRiskClassificationNodes, setEditDialogData: boolean = true) => {
        const selectedItem = RiskClassificationNode.getRiskClassificationNodeById(nodes.nodes, aState.selectedId);
        const selectedId = selectedItem ? selectedItem.id : "";
        const list = RiskClassificationNode.getAsList(aState.items);
        const index = list.findIndex(i => i.id === selectedId);
        return {
            items: nodes.nodes,
            selectedId,
            selectedCanBeRemoved: selectedItem ? selectedItem.children.length < 1 : false,
            editDialogId: setEditDialogData ? (selectedItem?.id ?? "") : aState.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : aState.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? index < list.length - 1 : aState.editDialogHasNext,
            editDialogParentId: setEditDialogData ? (selectedItem?.parentId ?? "") : aState.editDialogParentId,
        };
    };

    const [state, setState] = useState<IRiskClassificationNodesTreeState>({
        items: [],
        selectedId: "",
        selectedCanBeRemoved: false,
        showEditDialog: false,
        editDialogId: "",
        editDialogParentId: "",
        editDialogHasPrevious: false,
        editDialogHasNext: false,
        title: props.title,
        titleEditMode: false
    });
        
    const searchItems = useCallback(async (setEditDialogData: boolean = true) => {
        const method = props.riskClassificationType === RiskClassificationType.RiskCategory
            ? riskService.getRiskCategoryRiskClassificationNodes
            : (props.riskClassificationType === RiskClassificationType.RiskProcess
                ? riskService.getRiskProcessRiskClassificationNodes
                : (props.riskClassificationType === RiskClassificationType.RiskStandard
                    ? riskService.getRiskStandardRiskClassificationNodes
                    : (props.riskClassificationType === RiskClassificationType.RiskStakeholder
                        ? riskService.getRiskStakeholderRiskClassificationNodes
                        : undefined)));
        if (!method) return;
        const result = await AppUtils.callService(() => method(props.companyId), false);
        if (!result) return;
        setState(old => ({
            ...old,
            ...getStateFromRiskClassificationNodes(old, result, setEditDialogData)
        }));
    }, [props.companyId, props.riskClassificationType]);

    const refreshTree = async (setDialogData: boolean = true) => {
        await searchItems(setDialogData);
    };

    useEffect(() => {
        searchItems();
    }, [props.companyId]);

    useEffect(() => {
        setState(old => ({
            ...old,
            title: props.title
        }));
    }, [props.title]);

    const handleNodeClick = (id: string) => {
        if (!id) return;
        const selectedItem = RiskClassificationNode.getRiskClassificationNodeById(state.items, id);
        if (!selectedItem) return;
        setState(old => ({
            ...old,
            selectedId: selectedItem ? selectedItem.id : "",
            selectedCanBeRemoved: selectedItem ? selectedItem.children.length < 1 : false,
        }));
    };

    const handleSetCollapsed = (id: string, value: boolean) => {
        if (!id) return;
        const items = state.items.map(i => new RiskClassificationNode(i));
        const selectedItem = RiskClassificationNode.getRiskClassificationNodeById(items, id);
        if (!selectedItem) return;
        selectedItem.collapsed = value;
        setState(old => ({
            ...old,
            items,
            selectedId: selectedItem.id,
            selectedCanBeRemoved: selectedItem.children.length < 1,
        }));
    };

    const getRemoveSaveData = (props: IRiskClassificationNodesTreeProp, ids: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("ids", ids);
        return result;
    };

    const remove = async (id: string): Promise<ApiSuccess> => {
        if (!id) return;
        const saveData = getRemoveSaveData(props, [id]);
        // Call server
        const method = props.riskClassificationType === RiskClassificationType.RiskCategory
            ? riskService.removeRiskCategories
            : (props.riskClassificationType === RiskClassificationType.RiskProcess
                ? riskService.removeRiskProcesses
                : (props.riskClassificationType === RiskClassificationType.RiskStandard
                    ? riskService.removeRiskStandards
                    : (props.riskClassificationType === RiskClassificationType.RiskStakeholder
                        ? riskService.removeRiskStakeholders
                        : undefined)));
        const result = await AppUtils.callService(() => method(saveData.obj));
        if (!result) return null;
        refreshTree();
        return result;
    };

    const handleRemove = async () => {
        if (!state.selectedId) return;
        const selectedItem = RiskClassificationNode.getRiskClassificationNodeById(state.items, state.selectedId);
        if (!selectedItem) return;
        const removeConfirmationText = props.riskClassificationType === RiskClassificationType.RiskCategory
            ? Translations.DoYouReallyWantToRemoveRiskCategoryParameter
            : (props.riskClassificationType === RiskClassificationType.RiskProcess
                ? Translations.DoYouReallyWantToRemoveRiskProcessParameter
                : (props.riskClassificationType === RiskClassificationType.RiskStandard
                    ? Translations.DoYouReallyWantToRemoveRiskStandardParameter
                    : (props.riskClassificationType === RiskClassificationType.RiskStakeholder
                        ? Translations.DoYouReallyWantToRemoveRiskStakeholderParameter
                        : "")));
        if (await AppUtils.showConfirmationDialog(String.format(removeConfirmationText, selectedItem.code), false) === ConfirmationDialogResult.Yes) {
            await remove(selectedItem.id);
        }
    };

    const handleShowEditDialog = (id: string, eventTypeParentId: string, selectedCanBeRemoved: boolean) => {
        const isNew = id === Base.emptyGuid;
        const list = RiskClassificationNode.getAsList(state.items);
        const index = isNew ? 0 : list.findIndex(i => i.id === id);
        if (index < 0) return;
        setState(old => ({
            ...old,
            showEditDialog: true,
            selectedId: id,
            selectedCanBeRemoved: isNew ? state.selectedCanBeRemoved : selectedCanBeRemoved,
            editDialogId: id,
            editDialogParentId: isNew ? eventTypeParentId : "",
            editDialogHasPrevious: index > 0,
            editDialogHasNext: isNew ? false : index < list.length - 1
        }));
    };

    const handleEditDialogOk = () => {
        refreshTree(false);
    };

    const handleEditDialogCancel = (doRefreshTree: boolean) => {
        setState(old => ({
            ...old,
            showEditDialog: false
        }));
        if (!doRefreshTree) return;
        refreshTree(false);
    };

    const handleAdd = () => {
        handleShowEditDialog(Base.emptyGuid, "", null);
    };

    const handleAddSub = () => {
        if (!state.selectedId) return;
        const selectedItem = RiskClassificationNode.getRiskClassificationNodeById(state.items, state.selectedId);
        if (!selectedItem) return;
        handleShowEditDialog(Base.emptyGuid, selectedItem.id, null);
    };
    
    const handleOpen = (id: string) => {
        if (!id) return;
        const selectedItem = RiskClassificationNode.getRiskClassificationNodeById(state.items, id);
        if (!selectedItem) return;
        handleShowEditDialog(id, "", selectedItem.children.length < 1);
    };

    const setEditDialogItemByIndex = (index: number, list: IRiskClassificationNode[]) => {
        if (index < 0 || index >= list.length) return;
        setState(old => ({
            ...old,
            selectedId: list[index].id,
            editDialogId: list[index].id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < list.length - 1,
            editDialogParentId: list[index].parentId,
            selectedCanBeRemoved: list[index].children.length < 1
        }));
    };

    const handleMoveToPrevious = () => {
        if (!state.selectedId) return;
        const selectedItem = RiskClassificationNode.getRiskClassificationNodeById(state.items, state.selectedId);
        if (!selectedItem) return;
        const list = RiskClassificationNode.getAsList(state.items);
        const index = list.findIndex(i => i.id === selectedItem.id);
        if (index < 0) return;
        setEditDialogItemByIndex(index - 1, list);
    };

    const handleMoveToNext = () => {
        if (!state.selectedId) return;
        const selectedItem = RiskClassificationNode.getRiskClassificationNodeById(state.items, state.selectedId);
        if (!selectedItem) return;
        const list = RiskClassificationNode.getAsList(state.items);
        const index = list.findIndex(i => i.id === selectedItem.id);
        if (index < 0) return;
        setEditDialogItemByIndex(index + 1, list);
    };

    const items = state.items;

    if (!items) return null;

    const title = props.riskClassificationType === RiskClassificationType.RiskCategory
        ? (props.companyId ? Translations.RiskCategories : Translations.DefaultRiskCategories)
        : (props.riskClassificationType === RiskClassificationType.RiskProcess
            ? (props.companyId ? Translations.RiskProcesses : Translations.DefaultRiskProcesses)
            : (props.riskClassificationType === RiskClassificationType.RiskStandard
                ? (props.companyId ? Translations.RiskStandards : Translations.DefaultRiskStandards)
                : (props.riskClassificationType === RiskClassificationType.RiskStakeholder
                    ? (props.companyId ? Translations.RiskStakeholders : Translations.DefaultRiskStakeholders)
                    : "")));
    const addTooltip = props.riskClassificationType === RiskClassificationType.RiskCategory
        ? Translations.AddRiskCategory
        : (props.riskClassificationType === RiskClassificationType.RiskProcess
            ? Translations.AddRiskProcess
            : (props.riskClassificationType === RiskClassificationType.RiskStandard
                ? Translations.AddRiskStandard
                : (props.riskClassificationType === RiskClassificationType.RiskStakeholder
                    ? Translations.AddRiskStakeholder
                    : "")));
    const addSubTooltip = props.riskClassificationType === RiskClassificationType.RiskCategory
        ? Translations.AddChildRiskCategory
        : (props.riskClassificationType === RiskClassificationType.RiskProcess
            ? Translations.AddChildRiskProcess
            : (props.riskClassificationType === RiskClassificationType.RiskStandard
                ? Translations.AddChildRiskStandard
                : (props.riskClassificationType === RiskClassificationType.RiskStakeholder
                    ? Translations.AddChildRiskStakeholder
                    : "")));
    const removeTooltip = props.riskClassificationType === RiskClassificationType.RiskCategory
        ? Translations.RemoveRiskCategory
        : (props.riskClassificationType === RiskClassificationType.RiskProcess
            ? Translations.RemoveRiskProcess
            : (props.riskClassificationType === RiskClassificationType.RiskStandard
                ? Translations.RemoveRiskStandard
                : (props.riskClassificationType === RiskClassificationType.RiskStakeholder
                    ? Translations.RemoveRiskStakeholder
                    : "")));
    
    return (
        <>
            <DialogSettingsCard
                fitToContainerHeight
                title={!props.companyId || !props.titleTitle
                    ? title
                    : <Box sx={{ display: "flex", alignItems: "center" }}>
                        {(state.titleEditMode
                            ? <TextField
                                type="text"
                                placeholder={props.titleTitle}
                                value={state.title}
                                autoFocus
                                size="small"
                                sx={{ maxWidth: "400px" }}
                                fullWidth
                                onChange={(e) => setState((prev) => ({ ...prev, title: e.target.value }))}
                                inputProps={{
                                    maxLength: 50,
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <>
                                            <Tooltip title={Translations.Save}>
                                                <AppButton
                                                    variant="outlined"
                                                    onClick={() => {
                                                        props.onSaveInUseAndTitle(props.inUse, state.title);
                                                        setState((prev) => ({ ...prev, titleEditMode: false }));
                                                    }}
                                                    sx={{ borderRadius: 0, padding: "7px 9px", minWidth: "50px" }}
                                                >
                                                    <AppSaveIcon />
                                                </AppButton>
                                            </Tooltip>
                                            <Tooltip title={Translations.Cancel}>
                                                <span>
                                                    <AppButton
                                                        variant="outlined"
                                                        onClick={() => {
                                                            setState((prev) => ({ ...prev, titleEditMode: false }));
                                                        }}
                                                        sx={{ marginRight: "-14px", borderRadius: "0 .25rem .25rem 0", padding: "7px 9px", minWidth: "50px" }}
                                                    >
                                                        <AppCancelIcon />
                                                    </AppButton>
                                                </span>
                                            </Tooltip>
                                        </>
                                    )
                                }}
                            />
                            : <>
                                <Box component="span">{state.title}</Box>
                                <EditToolButton
                                    sx={{ marginLeft: ".5rem" }}
                                    onClick={() => {
                                        setState((prev) => ({ ...prev, titleEditMode: true }));
                                    }}
                                />
                            </>
                        )}
                    </Box>}
                info={props.infoText}
                actions={props.companyId && props.onSaveInUseAndTitle
                    ? <FormControlLabel
                        label={props.inUseTitle}
                        control={
                            <Checkbox
                                title={props.inUseTitle}
                                checked={props.inUse}
                                onChange={(_e, inUse: boolean) => {
                                    props.onSaveInUseAndTitle(inUse, props.title);
                                }}
                            />
                        }
                    />
                    : null}
                cardContent={<>
                    <ActionContainer
                        leftItems={
                            <></>
                        }
                        rightItems={
                            <Box sx={{ marginLeft: ".5rem", display: "flex" }}>
                                <AddToolButton
                                    tooltip={addTooltip}
                                    onClick={handleAdd}
                                />
                                <ToolButton
                                    variant="icon"
                                    tooltip={addSubTooltip}
                                    disabled={!state.selectedId}
                                    icon={<PlaylistAddIcon />}
                                    onClick={handleAddSub}
                                />
                                <RemoveToolButton
                                    disabled={!state.selectedId || !state.selectedCanBeRemoved}
                                    tooltip={removeTooltip}
                                    onClick={handleRemove}
                                />
                            </Box>
                        }
                    />
                </>}
                content={
                    <VerticalContainer
                        before={
                            <ListHeaderLine
                                columns={[
                                    { size: 5, column: "name", content: Translations.Name },
                                    { size: 5, column: "description", content: Translations.Description },
                                    { size: 2, column: "inUse", content: Translations.InUse },
                                ]}
                            />
                        }
                        main={
                            <Box sx={{
                                height: "100%",
                                overflowX: "hidden",
                                overflowY: "auto",
                            }}>
                                {items.map((item) =>
                                    <RiskClassificationTreeNode
                                        key={item.id}
                                        item={item}
                                        level={1}
                                        selectedId={state.selectedId}
                                        onClick={handleNodeClick}
                                        onDoubleClick={handleOpen}
                                        onSetCollapsed={handleSetCollapsed}
                                    />
                                )}
                            </Box>
                        }
                    />
                }
            />
            {props.riskClassificationType === RiskClassificationType.RiskCategory && state.showEditDialog &&
                <RiskCategoryDialog
                    id={state.editDialogId}
                    companyId={props.companyId}
                    parentId={state.editDialogParentId}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    onSaved={handleEditDialogOk}
                    onClose={handleEditDialogCancel}
                    onRemove={remove}
                    onRefreshList={() => refreshTree(false)}
                    onPrevious={handleMoveToPrevious}
                    onNext={handleMoveToNext}
                />
            }
            {props.riskClassificationType === RiskClassificationType.RiskProcess && state.showEditDialog &&
                <RiskProcessDialog
                    id={state.editDialogId}
                    companyId={props.companyId}
                    parentId={state.editDialogParentId}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    onSaved={handleEditDialogOk}
                    onClose={handleEditDialogCancel}
                    onRemove={remove}
                    onRefreshList={() => refreshTree(false)}
                    onPrevious={handleMoveToPrevious}
                    onNext={handleMoveToNext}
                />
            }
            {props.riskClassificationType === RiskClassificationType.RiskStandard && state.showEditDialog &&
                <RiskStandardDialog
                    id={state.editDialogId}
                    companyId={props.companyId}
                    parentId={state.editDialogParentId}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    onSaved={handleEditDialogOk}
                    onClose={handleEditDialogCancel}
                    onRemove={remove}
                    onRefreshList={() => refreshTree(false)}
                    onPrevious={handleMoveToPrevious}
                    onNext={handleMoveToNext}
                />
            }
            {props.riskClassificationType === RiskClassificationType.RiskStakeholder && state.showEditDialog &&
                <RiskStakeholderDialog
                    id={state.editDialogId}
                    companyId={props.companyId}
                    parentId={state.editDialogParentId}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    onSaved={handleEditDialogOk}
                    onClose={handleEditDialogCancel}
                    onRemove={remove}
                    onRefreshList={() => refreshTree(false)}
                    onPrevious={handleMoveToPrevious}
                    onNext={handleMoveToNext}
                />
            }
        </>
    );
};
